import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { HandHeart, ArrowLeft, Bell, MoreVertical } from "lucide-react";
import { useObligaciones } from "@/hooks/useObligaciones";
import StatsCards from "@/components/dashboard/StatsCards";
import ObligacionesTable from "@/components/dashboard/ObligacionesTable";
import { useAuth } from "@/hooks/useAuth";
import Footer from "@/components/Footer";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const GestorSocial = () => {
  const navigate = useNavigate();
  const { obligaciones, loading, getEstadisticas, loadObligaciones } = useObligaciones('Social');
  const { signOut, user } = useAuth();
  const stats = getEstadisticas();
  const [cardFilter, setCardFilter] = useState<'all' | 'Cumplido' | 'En Proceso' | 'Pendiente' | 'Vencido' | 'ProximasVencer'>('all');
  const [isAlertasOpen, setIsAlertasOpen] = useState(false);

  const handleCardFilterClick = (filterType: 'all' | 'Cumplido' | 'En Proceso' | 'Pendiente' | 'Vencido' | 'ProximasVencer') => {
    setCardFilter(filterType);
  };

  const getProximasAVencer = () => {
    const today = new Date();
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(today.getDate() + 30);

    return obligaciones.filter(o => {
      if (!o.vencimiento || o.estado === 'Cumplido') return false;
      const vencimiento = new Date(o.vencimiento);
      return vencimiento >= today && vencimiento <= thirtyDaysFromNow;
    }).sort((a, b) => new Date(a.vencimiento).getTime() - new Date(b.vencimiento).getTime());
  };

  const proximasAVencer = getProximasAVencer();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4 py-4">
          {/* Mobile/Tablet Layout */}
          <div className="lg:hidden relative flex flex-col items-center gap-1">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" className="absolute top-0 right-0">
                  <MoreVertical className="w-5 h-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 bg-background z-50">
                <DropdownMenuItem onClick={() => navigate("/social")}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Volver
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setIsAlertasOpen(true)}>
                  <Bell className="w-4 h-4 mr-2" />
                  Alertas
                  {proximasAVencer.length > 0 && (
                    <span className="ml-2">({proximasAVencer.length})</span>
                  )}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={signOut}>
                  Cerrar Sesión
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <HandHeart className="w-10 h-10 text-accent" />
            <div className="text-center">
              <h1 className="text-xl font-bold">Gestor Social</h1>
              <p className="text-sm text-muted-foreground">Sistema de Seguimiento</p>
            </div>
          </div>

          {/* Desktop Layout */}
          <div className="hidden lg:flex h-16 items-center justify-between">
            <div className="flex items-center gap-3">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigate("/social")}
              >
                <ArrowLeft className="w-6 h-6" />
              </Button>
              <HandHeart className="w-8 h-8 text-accent" />
              <div>
                <h1 className="text-xl font-bold">Gestor Social</h1>
                <p className="text-sm text-muted-foreground">Sistema de Seguimiento</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsAlertasOpen(true)}
                className="relative"
              >
                <Bell className="w-4 h-4 mr-2" />
                Alertas
                {proximasAVencer.length > 0 && (
                  <Badge variant="destructive" className="ml-2">
                    {proximasAVencer.length}
                  </Badge>
                )}
              </Button>
              <Button variant="outline" size="sm" onClick={signOut}>
                Cerrar Sesión
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto p-4 space-y-6">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-foreground mb-2">
            Dashboard de Gestión Social
          </h2>
          <p className="text-lg text-muted-foreground">
            Sistema de seguimiento y control de obligaciones sociales
          </p>
        </div>

        <StatsCards
          stats={stats} 
          onFilterClick={handleCardFilterClick}
          activeFilter={cardFilter}
        />

        <ObligacionesTable
          obligaciones={obligaciones}
          onRefresh={loadObligaciones}
          cardFilter={cardFilter}
          onClearFilter={() => setCardFilter('all')}
        />
      </main>

      <Dialog open={isAlertasOpen} onOpenChange={setIsAlertasOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Obligaciones Próximas a Vencer</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {proximasAVencer.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                No hay obligaciones próximas a vencer
              </p>
            ) : (
              proximasAVencer.map((obligacion) => {
                const diasRestantes = Math.ceil((new Date(obligacion.vencimiento).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
                return (
                  <Card key={obligacion.id} className="hover:bg-accent/5 transition-colors">
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-base">{obligacion.codigo}</CardTitle>
                          <CardDescription>{obligacion.nombre_programa}</CardDescription>
                        </div>
                        <Badge variant={obligacion.estado === 'Vencido' ? 'destructive' : 'secondary'}>
                          {obligacion.estado}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="text-muted-foreground">
                          Vence: {new Date(obligacion.vencimiento).toLocaleDateString()}
                        </span>
                        <Badge variant={diasRestantes <= 7 ? 'destructive' : 'outline'}>
                          {diasRestantes} días restantes
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        </DialogContent>
      </Dialog>
      <Footer />
    </div>
  );
};

export default GestorSocial;
